import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { fetchData } from "../../api/client";
import { clientFormSectionBodySx, clientFormSectionHeaderSx, clientFormSubsectionTitleSx } from "./clientFormStyles";
import { usePortalClienteChannelConfigured } from "../../hooks/usePortalClienteChannelConfigured";

function buildPortalPublicUrl(companySlug) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slug = String(
    companySlug ||
      (typeof window !== "undefined" ? window.localStorage.getItem("company") : "") ||
      ""
  ).trim();
  if (!slug) return `${origin}/portal-cliente`;
  return `${origin}/portal-cliente?company=${encodeURIComponent(slug)}`;
}

export default function ClientPortalSection({
  clientId,
  email,
  acessoPortal,
  hasPassword,
  onAcessoChange,
  companySlug,
  compact = false,
  accessSaving = false,
  sectionCardSx,
  embeddedInTab = false,
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const { configured: portalChannelConfigured, isLoading: portalChannelLoading } =
    usePortalClienteChannelConfigured();

  const portalUrl = useMemo(() => buildPortalPublicUrl(companySlug), [companySlug]);

  useEffect(() => {
    setErr(null);
    setMsg(null);
  }, [acessoPortal]);

  const statusChip = useMemo(() => {
    if (!acessoPortal) return { label: "Sem acesso", color: "default" };
    if (hasPassword) return { label: "Acesso ativo", color: "success" };
    return { label: "Aguardando senha", color: "warning" };
  }, [acessoPortal, hasPassword]);

  const handleInvite = async () => {
    if (!clientId) {
      setErr("Salve o cadastro antes de enviar o convite.");
      return;
    }
    if (!acessoPortal) {
      setErr("Ative o acesso ao portal antes de enviar o convite.");
      return;
    }
    if (!email?.trim()) {
      setErr("Informe um e-mail de contato para enviar o convite.");
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await fetchData(`/clients/${clientId}/portal/invite`, "POST", {});
      setMsg("Convite enviado. O cliente receberá um e-mail para definir a senha.");
    } catch (e) {
      const apiMsg = e?.message || e?.data?.message || "";
      if (String(apiMsg).includes("Libere o acesso")) {
        setErr("Confirme o acesso ao portal antes de enviar o convite.");
      } else {
        setErr(apiMsg || "Não foi possível enviar o convite.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAccess = async (event) => {
    const next = event.target.checked ? 1 : 0;
    setErr(null);
    try {
      await onAcessoChange?.(next);
    } catch {
      // Erro tratado pelo formulário pai
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Não foi possível copiar o link.");
    }
  };

  if (portalChannelLoading || !portalChannelConfigured) {
    return null;
  }

  const inner = (
    <Stack spacing={2.25}>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(acessoPortal)}
              onChange={handleToggleAccess}
              color="primary"
              disabled={accessSaving}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Liberar acesso ao Portal do Cliente
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                Permite que o cliente acompanhe chamados e abra solicitações com e-mail e senha.
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
        />
        {accessSaving ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ml: 6 }}>
            Atualizando permissão…
          </Typography>
        ) : null}
      </Box>

      {!clientId && acessoPortal ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Salve o cadastro para confirmar o acesso ao portal.
        </Alert>
      ) : null}

      {acessoPortal && !email?.trim() ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Informe um e-mail na seção Contato para liberar o portal e enviar convites.
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
          Situação
        </Typography>
        <Chip size="small" label={statusChip.label} color={statusChip.color} variant="outlined" />
      </Stack>

      <TextField
        size="small"
        label="Link público do portal"
        value={portalUrl}
        InputProps={{ readOnly: true }}
        helperText="Compartilhe este endereço ou envie o convite por e-mail."
        fullWidth
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          size="small"
        >
          {copied ? "Link copiado" : "Copiar link"}
        </Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
          onClick={handleInvite}
          disabled={!acessoPortal || !clientId || !email?.trim() || busy || accessSaving}
          size="small"
        >
          Enviar convite
        </Button>
      </Stack>

      {msg ? <Alert severity="success" sx={{ borderRadius: 2 }}>{msg}</Alert> : null}
      {err ? <Alert severity="error" sx={{ borderRadius: 2 }}>{err}</Alert> : null}
    </Stack>
  );

  if (compact || embeddedInTab) {
    return (
      <Box sx={{ mt: embeddedInTab ? 0 : 2 }}>
        {embeddedInTab ? (
          <Typography sx={clientFormSubsectionTitleSx} component="p">
            Portal do Cliente
          </Typography>
        ) : null}
        {inner}
      </Box>
    );
  }

  const cardSx = sectionCardSx || { mb: 2, borderRadius: 2.5 };

  return (
    <Card id="section-portal-cliente" variant="outlined" sx={{ ...cardSx, mb: 2 }}>
      <CardHeader
        title="Portal do Cliente"
        subheader="Área restrita para acompanhar chamados e abrir solicitações."
        sx={clientFormSectionHeaderSx}
      />
      <CardContent sx={clientFormSectionBodySx}>{inner}</CardContent>
    </Card>
  );
}
